import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ICallFlowStep,
  IClinicInternalExtension,
  IClinicNumber,
} from '@dataTypes/clinic.type'
import { Select } from '@enamelai/design-system'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import StepPanelShell from './StepPanelContainer.component'

const EXTENSION_NA = 'N/A' as const

const isUnsetOrNA = (v: string | null | undefined): boolean =>
  v == null || String(v).trim() === '' || String(v).trim() === 'N/A'

const phoneKeyEqual = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  if (!b || b === 'N/A' || b === '') return a === null
  return normalizePhoneNumber(a ?? '') === normalizePhoneNumber(b)
}

export type DialNumberPanelProps = {
  step: Extract<ICallFlowStep, { type: 'dial_number' }>
  branchLabel: string
  clinicNumbers: IClinicNumber[]
  internalExtensions: IClinicInternalExtension[]
  onChange: (
    patch: Partial<Extract<ICallFlowStep, { type: 'dial_number' }>>,
  ) => void
  onCancel: () => void
  onDone: () => void
}

const DialNumberPanel = ({
  step,
  branchLabel,
  clinicNumbers,
  internalExtensions,
  onChange,
  onCancel,
  onDone,
}: DialNumberPanelProps): React.ReactElement => {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const hasPhone = Boolean(step.phoneNumber?.trim())
  const bothPhoneAndExtensionUnset =
    isUnsetOrNA(step.phoneNumber) && isUnsetOrNA(step.extension)

  const phoneOptions = useMemo(() => {
    const fromClinic = [
      { label: 'N/A', value: 'N/A' },
      ...clinicNumbers
        .filter((n) => n.phone_number)
        .map((n) => ({
          label: formatPhoneNumber(n.phone_number),
          value: n.phone_number,
        })),
    ]
    const current = step.phoneNumber
    if (current && !fromClinic.some((o) => o.value === current)) {
      return [
        {
          label: formatPhoneNumber(current) || current,
          value: current,
        },
        ...fromClinic,
      ]
    }
    return fromClinic
  }, [clinicNumbers, step.phoneNumber])

  const extensionsForPhone = useMemo(() => {
    const pn = step.phoneNumber ?? ''
    return internalExtensions
      .filter((e) => e.extension_number && phoneKeyEqual(e.phone_number, pn))
      .sort((a, b) =>
        a.extension_number.localeCompare(b.extension_number, undefined, {
          numeric: true,
        }),
      )
  }, [internalExtensions, step.phoneNumber])

  const lineExtensionForSelected = useMemo(() => {
    if (!step.phoneNumber?.trim()) return undefined
    const line = clinicNumbers.find(
      (n) => n.phone_number && phoneKeyEqual(n.phone_number, step.phoneNumber!),
    )
    return line?.extension_number?.trim() || undefined
  }, [clinicNumbers, step.phoneNumber])

  const hasNoRegisterableExtension = useMemo(
    () =>
      hasPhone && extensionsForPhone.length === 0 && !lineExtensionForSelected,
    [hasPhone, extensionsForPhone, lineExtensionForSelected],
  )

  const extensionOptions = useMemo(() => {
    if (!hasPhone || !step.phoneNumber?.trim()) return []
    const fromInternal = extensionsForPhone.map((e) => ({
      label: e.extension_number,
      value: e.extension_number,
    }))
    const selectedLine = clinicNumbers.find((n) =>
      phoneKeyEqual(n.phone_number, step.phoneNumber!),
    )
    const lineExt = lineExtensionForSelected
    const base = [...fromInternal]
    if (lineExt && !base.some((o) => o.value === lineExt)) {
      base.unshift({
        label: selectedLine?.line_name?.trim()
          ? `${selectedLine.line_name.trim()} — ${lineExt}`
          : lineExt,
        value: lineExt,
      })
    }
    if (base.length === 0) {
      return [{ label: 'N/A', value: EXTENSION_NA }]
    }
    const ext = step.extension
    if (ext && ext !== EXTENSION_NA && !base.some((o) => o.value === ext)) {
      return [{ label: ext, value: ext }, ...base]
    }
    return base
  }, [
    hasPhone,
    step.extension,
    step.phoneNumber,
    clinicNumbers,
    extensionsForPhone,
    lineExtensionForSelected,
  ])

  useLayoutEffect(() => {
    if (!hasPhone) return
    if (hasNoRegisterableExtension) {
      if (step.extension !== EXTENSION_NA) {
        onChangeRef.current({ extension: EXTENSION_NA })
      }
    } else if (step.extension === EXTENSION_NA) {
      onChangeRef.current({ extension: undefined })
    }
  }, [hasPhone, step.extension, hasNoRegisterableExtension])

  const extOnlyNa =
    hasPhone &&
    extensionOptions.length === 1 &&
    extensionOptions[0]!.value === EXTENSION_NA

  const extDropdownValue = useMemo(() => {
    if (!hasPhone) return null
    if (extensionOptions.length === 0) return null
    if (extOnlyNa) return EXTENSION_NA
    const v = step.extension
    if (!v || v === EXTENSION_NA) return null
    return v
  }, [hasPhone, extOnlyNa, step.extension, extensionOptions])

  const handleStepPatch = (
    patch: Partial<Extract<ICallFlowStep, { type: 'dial_number' }>>,
  ): void => {
    onChange(patch)
  }

  const handleDone = (): void => {
    if (bothPhoneAndExtensionUnset) {
      return
    }
    onDone()
  }

  return (
    <StepPanelShell
      title="Fwd. call"
      branchLabel={branchLabel}
      onCancel={onCancel}
      onDone={handleDone}
    >
      <div className="scp-row">
        <div className="scp-panel-field">
          <Select
            label="Phone number"
            options={phoneOptions}
            value={step.phoneNumber || undefined}
            placeholder={
              phoneOptions.length === 0
                ? 'No phone lines in phone management'
                : 'Select phone number'
            }
            onChange={(value) => {
              const next = String(value)
              handleStepPatch({ phoneNumber: next, extension: undefined })
            }}
          />
        </div>
        <div
          className="scp-panel-field dial-number-extension"
          title={!hasPhone ? 'Select phone number' : undefined}
        >
          <div
            className={!hasPhone ? 'scp-dial-ext-dd-disabled-wrap' : undefined}
          >
            <Select
              label="Extension no."
              options={extensionOptions}
              value={extDropdownValue || undefined}
              disabled={!hasPhone}
              placeholder={
                !hasPhone ? 'Select phone number first' : 'Select extension'
              }
              onChange={(value) =>
                handleStepPatch({
                  extension: String(value) || undefined,
                })
              }
            />
          </div>
        </div>
      </div>
      <span className="hint-text">
        {bothPhoneAndExtensionUnset
          ? 'Kindly select a phone number or extension number'
          : 'Configure handling for answered and unanswered calls.'}
      </span>
    </StepPanelShell>
  )
}

export default DialNumberPanel
